import { ColorInput } from '@/components/ColorInput';
import { SystemPreview } from '@/components/SystemPreview';
import BaseLayout from '@/layout/BaseLayout';
import { trpc, trpcClient } from '@/trpcClient';
import { hexToDarkRgba, hexToRgba, darkenColor, lightenColor, urlToFile } from '@/utils/utils';
import { checkTenantEditInput } from '@shelflix/schemas/tenant/edit';
import Dropzone from '@shelflix/ui-web/components/Dropzone/index';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useApplicationConfig } from './store';

import { Label } from '@shelflix/ui-web/components/ui/label';
import { toast } from 'sonner';

import shleflixLogo from '@/assets/shelflix-logo.png';
import { useConfigStore } from '@/store/configStore';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import { usePreventLeave } from '@/utils/usePreventLeave';

// Form type for application config page (uses tenant.edit schema fields)
interface ApplicationConfigFormType {
  themeColor: string;
  logoFileType: string;
  logoDarkFileType: string;
  faviconFileType: string;
}

export default function ApplicationConfig() {
  const [isUploading, setIsUploading] = useState(false);
  usePreventLeave(isUploading);

  const navigate = useNavigate();
  const { setConfig } = useConfigStore.getState();
  const { formData, resetForm } = useApplicationConfig();

  const form = useForm<ApplicationConfigFormType>({
    defaultValues: {
      themeColor: formData.themeColor || '',
      logoFileType: '',
      logoDarkFileType: '',
      faviconFileType: '',
    },
    mode: 'all',
  });

  // Use tenant.getActiveConfig to get current tenant's configuration
  const { data: currentTenantConfig } = useQuery(trpc.tenant.getActiveConfig.queryOptions());
  // Use tenant.edit to save changes to the current tenant
  const tenantEditMutation = useMutation(trpc.tenant.edit.mutationOptions());

  const { isMobile } = useDeviceDetect();

  const [logo, setLogo] = useState<File | null>(null);
  const [logoDark, setLogoDark] = useState<File | null>(null);
  const [favicon, setFavicon] = useState<File | null>(null);

  // Track which files were changed by the user (vs loaded from existing URLs)
  const [logoChanged, setLogoChanged] = useState(false);
  const [logoDarkChanged, setLogoDarkChanged] = useState(false);
  const [faviconChanged, setFaviconChanged] = useState(false);

  const primaryColor = form.watch('themeColor') ?? '';
  useEffect(() => {
    if (logo?.type !== form.getValues('logoFileType')) {
      form.setValue('logoFileType', logo?.type ?? '');
      form.trigger('logoFileType');
    }
    if (logoDark?.type !== form.getValues('logoDarkFileType')) {
      form.setValue('logoDarkFileType', logoDark?.type ?? '');
      form.trigger('logoDarkFileType');
    }
    if (favicon?.type !== form.getValues('faviconFileType')) {
      form.setValue('faviconFileType', favicon?.type ?? '');
      form.trigger('faviconFileType');
    }
  }, [logo, favicon, logoDark]);

  useEffect(() => {
    if (currentTenantConfig) {
      urlToFile(currentTenantConfig.logoUrl, 'default').then((file) => {
        setLogo(file);
      });
      if (currentTenantConfig.logoDarkUrl)
        urlToFile(currentTenantConfig.logoDarkUrl, 'default').then((file) => {
          if (file) {
            setLogoDark(file);
          }
        });

      urlToFile(currentTenantConfig.faviconUrl, 'default')
        .then((file) => {
          setFavicon(file);
        })
        .finally(() => {
          form.reset({
            themeColor: currentTenantConfig.themeColor || '',
            logoFileType: '',
            logoDarkFileType: '',
            faviconFileType: '',
          });
        });
    } else {
      const computedStyle = getComputedStyle(document.documentElement);
      const primaryColor = computedStyle.getPropertyValue('--primary').trim();

      form.reset({
        themeColor: primaryColor,
        logoFileType: '',
        logoDarkFileType: '',
        faviconFileType: '',
      });
    }
  }, [currentTenantConfig]);

  function saveTheme(data: ApplicationConfigFormType) {
    if (tenantEditMutation.isPending || !currentTenantConfig) return;

    // Only send file types for files that were actually changed by the user
    const payload = {
      uuid: currentTenantConfig.uuid,
      themeColor: data.themeColor,
      logoFileType: logoChanged && logo ? logo.type : undefined,
      logoDarkFileType: logoDarkChanged && logoDark ? logoDark.type : undefined,
      faviconFileType: faviconChanged && favicon ? favicon.type : undefined,
    };

    tenantEditMutation.mutate(payload, {
      onSuccess: async (response) => {
        try {
          const hasUploads = response.uploadUrls?.logo || response.uploadUrls?.logoDark || response.uploadUrls?.favicon;

          if (hasUploads) {
            setIsUploading(true);

            // Upload logo if upload URL provided and file was changed
            if (response.uploadUrls?.logo && logoChanged && logo) {
              const logoFormData = new FormData();
              Object.entries(response.uploadUrls.logo.fields ?? {}).forEach(([key, value]) => {
                logoFormData.append(key, value);
              });
              logoFormData.append('file', logo);

              await fetch(response.uploadUrls.logo.url, {
                method: 'POST',
                body: logoFormData,
              });
            }

            // Upload logoDark if upload URL provided and file was changed
            if (response.uploadUrls?.logoDark && logoDarkChanged && logoDark) {
              const logoDarkFormData = new FormData();
              Object.entries(response.uploadUrls.logoDark.fields ?? {}).forEach(([key, value]) => {
                logoDarkFormData.append(key, value);
              });
              logoDarkFormData.append('file', logoDark);

              await fetch(response.uploadUrls.logoDark.url, {
                method: 'POST',
                body: logoDarkFormData,
              });
            }

            // Upload favicon if upload URL provided and file was changed
            if (response.uploadUrls?.favicon && faviconChanged && favicon) {
              const faviconFormData = new FormData();
              Object.entries(response.uploadUrls.favicon.fields ?? {}).forEach(([key, value]) => {
                faviconFormData.append(key, value);
              });
              faviconFormData.append('file', favicon);

              await fetch(response.uploadUrls.favicon.url, {
                method: 'POST',
                body: faviconFormData,
              });
            }
          }
          resetForm();

          // Reset changed flags after successful save
          setLogoChanged(false);
          setLogoDarkChanged(false);
          setFaviconChanged(false);

          // Refresh tenant config to get updated presigned URLs
          const configResponse = await trpcClient.tenant.getActiveConfig.query();

          // Update global config store with new tenant config
          if (configResponse) {
            setConfig({
              uuid: configResponse.uuid,
              logoUrl: configResponse.logoUrl,
              logoDarkUrl: configResponse.logoDarkUrl,
              faviconUrl: configResponse.faviconUrl,
              themeColor: configResponse.themeColor,
              domain: configResponse.domain,
            });
          }

          const secondary = hexToRgba(data.themeColor, 0.1);
          const secondaryDark = hexToDarkRgba(data.themeColor, 0.1);

          document.documentElement.style.setProperty('--primary', data.themeColor);
          document.documentElement.style.setProperty('--secondary-light', secondary);
          document.documentElement.style.setProperty('--secondary-light-hover', darkenColor(secondary, 10));
          document.documentElement.style.setProperty('--secondary', secondary);
          document.documentElement.style.setProperty('--secondary-dark', secondaryDark);
          document.documentElement.style.setProperty('--secondary-hover', darkenColor(secondary, 10));
          document.documentElement.style.setProperty('--secondary-dark-hover', lightenColor(secondaryDark, 10));

          if (configResponse?.faviconUrl) {
            let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = configResponse.faviconUrl;
          }
          navigate(location.pathname, { replace: true, state: { toastSuccess: true } });
        } catch (error) {
          toast.error(`Erro no upload: ${error.message}`);
        } finally {
          setIsUploading(false);
        }
      },
      onError: (error) => {
        toast.error(`Erro ao cadastrar tema: ${error.message}`);
      },
    });
  }

  return (
    <BaseLayout>
      <div className="flex w-full flex-col gap-8 sm:flex-row">
        <Card className="basis-[50%] rounded-[10px]">
          <CardHeader className="pb-0">
            <CardTitle className="text-primary text-2xl leading-5 font-semibold">Identidade visual</CardTitle>
          </CardHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(saveTheme, scrollToError)}>
              <CardContent className="flex flex-col gap-8">
                <div
                  className="flex flex-col items-start justify-start gap-8 sm:flex-row"
                  style={{ alignItems: isMobile ? 'flex-start' : 'flex-end' }}
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-2">
                      <Label className="font-semibold">Logo</Label>
                      <FormField
                        control={form.control}
                        name="logoFileType"
                        rules={{ validate: (value) => checkTenantEditInput('logoFileType', value) }}
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-[3px]">
                            <FormControl>
                              <Dropzone
                                title="Arraste ou clique aqui para selecionar seu arquivo"
                                description="PNG ou SVG com fundo transparente (16:9)"
                                removable={false}
                                className="grow"
                                classNameContent="bg-white"
                                onFilesSelected={(files) => {
                                  setLogo(files[0]);
                                  setLogoChanged(true);
                                  field.onChange(files[0]?.type);
                                }}
                                value={logo ? [logo] : undefined}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label className="font-semibold">Logo Dark Mode</Label>
                      <FormField
                        control={form.control}
                        name="logoDarkFileType"
                        rules={{ validate: (value) => checkTenantEditInput('logoDarkFileType', value || '') }}
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-[3px]">
                            <FormControl>
                              <Dropzone
                                title="Arraste ou clique aqui para selecionar seu arquivo"
                                description="PNG ou SVG com fundo transparente (16:9)"
                                removable={false}
                                className="grow"
                                classNameContent="bg-black"
                                onFilesSelected={(files) => {
                                  setLogoDark(files[0]);
                                  setLogoDarkChanged(true);
                                  field.onChange(files[0]?.type);
                                }}
                                value={logoDark ? [logoDark] : undefined}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <FormField
                    control={form.control}
                    name="faviconFileType"
                    rules={{ validate: (value) => checkTenantEditInput('faviconFileType', value) }}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px]">
                        <Label className="font-semibold">Favicon</Label>
                        <FormControl>
                          <Dropzone
                            variant="square"
                            title="Favicon (1:1)"
                            showIcon={false}
                            requiredAspectRatio="1:1"
                            removable={false}
                            onFilesSelected={(files) => {
                              setFavicon(files[0]);
                              setFaviconChanged(true);
                              field.onChange(files[0]?.type);
                            }}
                            areaWidth="150"
                            value={favicon ? [favicon] : undefined}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  rules={{ validate: (value) => checkTenantEditInput('themeColor', value) }}
                  name="themeColor"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <Label className="font-semibold">Esquema de cores</Label>

                      {/* Row with labels */}
                      <div className="flex w-full">
                        <div className="w-1/2">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                            Código de cor primária
                          </FormLabel>
                        </div>
                        <div className="flex flex-1 items-center justify-evenly">
                          <span className="text-xs font-medium text-[var(--gray-primary)]">Primária</span>
                          <span className="text-xs font-medium text-[var(--gray-primary)]">Clara</span>
                          <span className="text-xs font-medium text-[var(--gray-primary)]">Escura</span>
                        </div>
                      </div>

                      {/* Row with input and color squares */}
                      <div className="flex items-center">
                        <div className="w-1/2">
                          <FormControl>
                            <ColorInput value={field.value} onChange={field.onChange} mode="picker" />
                          </FormControl>
                        </div>
                        <div className="flex flex-1 items-center justify-evenly">
                          {/* Cor primária */}
                          <div className="relative h-9 w-9 flex-shrink-0">
                            <div
                              className="absolute inset-0 rounded border"
                              style={{
                                backgroundColor: field.value || 'transparent',
                                borderColor: '#8C8787',
                              }}
                            />
                          </div>
                          {/* Cor clara */}
                          <div className="relative h-9 w-9 flex-shrink-0">
                            <div
                              className="absolute inset-0 rounded border"
                              style={{
                                backgroundColor: field.value ? hexToRgba(field.value, 0.1) : 'transparent',
                                borderColor: '#8C8787',
                              }}
                            />
                          </div>
                          {/* Cor escura */}
                          <div className="relative h-9 w-9 flex-shrink-0">
                            <div
                              className="absolute inset-0 rounded border"
                              style={{
                                backgroundColor: field.value ? hexToDarkRgba(field.value, 0.1) : 'transparent',
                                borderColor: '#8C8787',
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>

              {!isMobile && (
                <CardFooter className="mt-8 flex flex-row items-center justify-center">
                  <Button
                    type="submit"
                    className="cursor-pointer"
                    loading={tenantEditMutation.isPending || isUploading}
                  >
                    <Icon name="format_paint" variant="sharp" color="var(--white)" />
                    <span>Salvar tema</span>
                  </Button>
                </CardFooter>
              )}
            </form>
          </Form>
        </Card>

        <Card className="rounded-[10px] sm:w-[50%]">
          <CardHeader>
            <CardTitle className="text-primary text-2xl leading-5 font-semibold">Simulação de tema</CardTitle>
          </CardHeader>

          <CardContent>
            <SystemPreview
              primaryColor={primaryColor}
              secondary={hexToRgba(primaryColor, 0.1)}
              secondaryDark={hexToDarkRgba(primaryColor, 0.1)}
              logoUrl={logo ? URL.createObjectURL(logo) : shleflixLogo}
            />
          </CardContent>
          {isMobile && (
            <CardFooter className="flex w-full flex-row items-center justify-center">
              <Button
                onClick={() => form.handleSubmit(saveTheme)()}
                className="mt-6 w-full cursor-pointer"
                loading={tenantEditMutation.isPending || isUploading}
              >
                <Icon name="format_paint" variant="sharp" color="var(--white)" />
                <span>Salvar tema</span>
              </Button>
            </CardFooter>
          )}
        </Card>
      </div>
    </BaseLayout>
  );
}
